import React from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const TEAM = [
  { name: 'Dr. Sarah Mitchell', role: 'Head of Research & Quality', emoji: '👩‍🔬', bio: 'PhD in Biochemistry from MIT with 15+ years in peptide synthesis and quality assurance.' },
  { name: 'James Chen', role: 'Chief Operations Officer', emoji: '👨‍💼', bio: 'Former pharmaceutical logistics executive with deep expertise in cold-chain supply management.' },
  { name: 'Dr. Marcus Webb', role: 'Lead Peptide Chemist', emoji: '🧪', bio: 'Post-doctoral researcher specializing in SPPS and novel peptide analogue development.' },
  { name: 'Elena Rodriguez', role: 'Customer Success Lead', emoji: '💬', bio: 'Dedicated to ensuring every researcher has access to the support and information they need.' },
];

const MILESTONES = [
  { year: '2018', text: 'PeptideLabPro founded with a mission to bring pharmaceutical-grade research peptides to scientists worldwide' },
  { year: '2019', text: 'Launched third-party testing program with accredited independent laboratories' },
  { year: '2020', text: 'Expanded catalog to 200+ research compounds; served 1,000+ active researchers' },
  { year: '2022', text: 'Achieved ISO 9001 quality management certification; opened new synthesis facility' },
  { year: '2024', text: 'Surpassed 10,000 researcher accounts globally; launched Peptide Calculator and COA portal' },
];

export default function About() {
  return (
    <div className="about-page">
      <div className="page-hero">
        <div className="container">
          <h1>About PeptideLabPro</h1>
          <p>Driven by science. Built on integrity. Committed to research excellence.</p>
        </div>
      </div>

      {/* Mission */}
      <section className="section">
        <div className="container about-mission">
          <div>
            <h2 className="section-title">Our Mission</h2>
            <p>PeptideLabPro exists to empower researchers worldwide with access to the highest-quality, independently verified research peptides. We believe scientific progress depends on reliable, pure, and well-characterized compounds.</p>
            <p style={{ marginTop: '1rem' }}>Every product we supply undergoes rigorous third-party testing. We publish all Certificates of Analysis openly, because transparency isn't just a policy — it's who we are.</p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1.75rem' }}>
              <Link to="/shop" className="btn btn-primary">Shop Our Catalog</Link>
              <Link to="/certificates" className="btn btn-secondary">View Testing Reports</Link>
            </div>
          </div>
          <div className="about-stats">
            {[['500+','Research Compounds'],['10,000+','Global Researchers'],['99.2%','Average Purity'],['6+','Years of Excellence'],['100%','Third-Party Tested'],['48hr','Average Dispatch']].map(([n, l]) => (
              <div key={l} className="about-stat">
                <strong>{n}</strong><span>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section" style={{ background: 'var(--gray-50)' }}>
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="section-title">Our Core Values</h2>
          </div>
          <div className="grid-4">
            {[['🔬','Scientific Rigor','Every decision is grounded in peer-reviewed science and evidence-based practices.'],['🏆','Quality First','We never compromise on purity — 99%+ is our minimum, not our target.'],['🔒','Full Transparency','All COAs publicly accessible. No hidden information, no hidden agendas.'],['🤝','Researcher Partnership','We treat every client as a partner in advancing scientific knowledge.']].map(([icon, title, desc]) => (
              <div key={title} className="value-card card">
                <div className="value-icon">{icon}</div>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="section-title">Our Journey</h2>
          </div>
          <div className="timeline">
            {MILESTONES.map((m, i) => (
              <div key={i} className="timeline-item">
                <div className="timeline-year">{m.year}</div>
                <div className="timeline-dot" />
                <div className="timeline-text card"><p>{m.text}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section" style={{ background: 'var(--gray-50)' }}>
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="section-title">Meet the Team</h2>
            <p className="section-subtitle">Scientists and professionals dedicated to research excellence</p>
          </div>
          <div className="grid-4">
            {TEAM.map(m => (
              <div key={m.name} className="team-card card">
                <div className="team-avatar">{m.emoji}</div>
                <h3>{m.name}</h3>
                <p className="team-role">{m.role}</p>
                <p className="team-bio">{m.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
